import { checkIsOutStock } from '../../../../utils/useCheckIsOutStock';
import Button from '../../../atoms/button/Button';
import { PadlockStroke1 } from '../../../atoms/icons';
import PrimaryLoader from '../../../atoms/primaryLoader/PrimaryLoader';
import Price from '../../../molecules/price/Price';
import PromoCode from '../../../molecules/promoCode/index';
import { Text } from '../../../typography/text/Text';
import CartTotals from './CartTotals';
import styles from './DesktopMinicart.module.scss';
import stylesMinicart from '../MiniCart.module.scss';
import ProductList from './ProductList';
import app from '@frontastic/catwalk/src/js/app/app';
import React, { useEffect, useRef, useState, forwardRef, useMemo } from 'react';
import { useCartContext } from '../../../../provider/cart/CartContext';
import clsx from 'clsx';
import { FORM_TYPE } from '../../../molecules/account/accountDropdown/AccountDropdown';
import { useOpenSignForm } from '../../../../utils/useOpenSignForm';
import Notification from '../../../atoms/notification';
import { customClasses } from './_vars';
import { checkGiftCardPurchase } from '../../../../utils/checkGiftCardPurchase';
import NostoList from './NostoList';

const DesktopMinicart = forwardRef(
  (
    { data, loggedIn, firstOrderMessage, noProductsMessage, shippingCoastMessage, nostoBlocks, customerGroupId },
    ref,
  ) => {
    const { projectSpecificData, lineItems } = data;
    const [indent, setIndent] = useState(0);
    const [showBoxShadow, setShowBoxShadow] = useState(false);
    const [showNostoBlock, setShowNostoBlock] = useState(false);
    const [headerHeight, setHeaderHeight] = useState(null);
    const [footerHeight, setFooterHeight] = useState(null);
    const orderActionRef = useRef(null);
    const productListRef = useRef(null);
    const { signFormOpen } = useOpenSignForm();
    const { setShowCart, loading, setLoading } = useCartContext();
    const { isGiftCardPurchase } = checkGiftCardPurchase();
    const isLocked = lineItems && checkIsOutStock(lineItems) && !isGiftCardPurchase;
    const isEmptyCart = !data?.lineItems?.length;
    const fullPriceArray = [];
    const discountPriceArray = [];
    const cartErrors = data.projectSpecificData?.errors;

    lineItems &&
      lineItems.forEach((element) => {
        fullPriceArray.push(element.price);
        discountPriceArray.push(element.discountedPrice);
      });

    useEffect(() => {
      const orderActionHeight = orderActionRef.current.getBoundingClientRect().height;
      setIndent(orderActionHeight);
    }, []);

    const scrollbarVisible = (ref) => {
      if (!!window && ref) {
        const productListHeight = ref.firstElementChild?.clientHeight;
        const totalHeight = ref.lastElementChild?.clientHeight;
        const asideHeight = ref.closest('aside')?.clientHeight;

        const headerH = document.querySelector(`.${stylesMinicart.header}`)?.clientHeight;
        const footerH = orderActionRef.current.getBoundingClientRect().height;
        setHeaderHeight(headerH);
        setFooterHeight(footerH);
        return productListHeight + totalHeight > asideHeight - headerH - footerH;
      }
      return false;
    };

    useEffect(() => {
      if (!!data.lineItems && productListRef) {
        setShowBoxShadow(scrollbarVisible(productListRef.current));
      }
    }, [data.lineItems, productListRef, showNostoBlock]);

    useEffect(() => {
      window.addEventListener('resize', () => {
        if (!!data.lineItems && productListRef) {
          setShowBoxShadow(scrollbarVisible(productListRef.current));
        }
      });
      return () => {
        window.removeEventListener('resize', () => {
          if (!!data.lineItems && productListRef) {
            setShowBoxShadow(scrollbarVisible(productListRef.current));
          }
        });
      };
    }, []);

    const isAnyIsOutOfStock = useMemo(
      () => data?.lineItems?.some((i) => i.projectSpecificData?.availableQuantity === 0 || false),
      [data?.lineItems],
    );

    return (
      <div
        className={clsx(styles.root, { [styles.rootEmpty]: isEmptyCart })}
        ref={ref}
        style={{
          '--indent-height': indent && `${indent}px`,
          '--header-height': headerHeight && `${headerHeight}px`,
          '--footer-height': footerHeight && `${footerHeight}px`,
        }}
      >
        {loading && (
          <div className={styles.cartLoader}>
            <PrimaryLoader mode="global" />
          </div>
        )}
        <div
          className={clsx(styles.orderManagement, {
            [styles.boxShadow]: showBoxShadow,
          })}
        >
          {!isEmptyCart ? (
            <div className={styles.cartContent} ref={productListRef}>
              {!!isAnyIsOutOfStock && (
                <Notification
                  type="error"
                  className={styles.errorMessage}
                  message="Unfortunately, one or more of your order items have fallen out of stock.
                  Please review your order items below and remove the out of stock item."
                />
              )}
              {cartErrors?.length && (
                <div className={styles.errorMessage}>
                  <Notification type="error" message={cartErrors[0].message} />
                </div>
              )}
              <ProductList
                setLoading={setLoading}
                items={data.lineItems}
                isLoggedIn={loggedIn}
                setShowDialog={setShowCart}
                discounts={{
                  list: projectSpecificData?.discounts,
                  codes: projectSpecificData?.discountCodesOriginal,
                }}
              />
              <div className={styles.orderDetails}>
                <CartTotals
                  subTotal={projectSpecificData?.subTotal}
                  items={data.lineItems}
                  shippingPrice={data.shippingPrice}
                  shippingMessages={shippingCoastMessage}
                  isLoggedIn={loggedIn}
                  placement="minicart"
                  discounts={projectSpecificData?.discounts?.filter((d) => d.type === 'cart-discount')}
                  appliedVouchers={projectSpecificData?.applied_vouchers}
                  discountCodesOriginal={projectSpecificData?.discountCodesOriginal}
                />
                {!isGiftCardPurchase && (
                  <div className={styles.promoCodeBlock}>
                    <PromoCode showInformMessage customClasses={customClasses} type="transform" />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.emptyMessage}>Your cart is empty</div>
          )}

          <div className={clsx(styles.orderAction, { [styles.orderActionEmpty]: isEmptyCart })} ref={orderActionRef}>
            {!isEmptyCart ? (
              <div className={styles.summaryText}>
                <Text as="span" size="xs" weight="light" textTransform="firstletter" align="start">
                  Your Total:
                </Text>
                <Price placement="minicart" type="regular" value={data.sum} showCurrency />
              </div>
            ) : null}
            {lineItems && !lineItems.length && noProductsMessage && !isEmptyCart && (
              <div className={styles.noProductsMessage}>
                <Text as="span" size="xs" weight="normal" textTransform="none" align="center">
                  {noProductsMessage}
                </Text>
              </div>
            )}
            {!isEmptyCart && (
              <>
                <Button
                  className={styles.orderButton}
                  shape="square"
                  variant="primary"
                  isInverted={false}
                  isOutlined={false}
                  disabled={isLocked || cartErrors?.length}
                  data-proceed-button="proceed_to_checkout"
                  submit={true}
                  as="button"
                  onClick={() => {
                    setShowCart(false);
                    app.getRouter().push('Frontastic.Frontend.Master.Checkout.checkout');
                  }}
                >
                  <Button.Label>Proceed to checkout</Button.Label>
                  <PadlockStroke1 width="24" height="22" />
                </Button>
                {!loggedIn && (
                  <div
                    className={styles.signUpMessage}
                    onClick={() => {
                      signFormOpen(FORM_TYPE.REGISTER);
                      setShowCart(false);
                    }}
                  >
                    <Text as="span">{firstOrderMessage}</Text>
                  </div>
                )}
              </>
            )}

            <NostoList
              {...nostoBlocks}
              setShowNostoBlock={setShowNostoBlock}
              asSlider={!isEmptyCart}
              customerGroupId={customerGroupId}
              loggedIn={loggedIn}
              isEmptyCart={isEmptyCart}
              setLoading={setLoading}
              setShowDialog={setShowCart}
              discounts={{
                list: projectSpecificData?.discounts,
                codes: projectSpecificData?.discountCodesOriginal,
              }}
            />
          </div>
        </div>
      </div>
    );
  },
);

export default DesktopMinicart;
